import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { NotificationDto } from 'src/app/core/data-models/notification.data.models';
import { NotificationDataService } from 'src/app/core/data-services/notification/notification-data.service';
import { LanguageService } from 'src/app/shared/services/language.service';

const PAGE_SIZE = 20;

@Component({
  selector: 'app-notification-sidebar',
  templateUrl: './notification-sidebar.component.html',
  styleUrls: ['./notification-sidebar.component.css'],
})
export class NotificationSidebarComponent {
  @Input() isRtl = false;

  sidebarVisible = false;
  notifications: NotificationDto[] = [];
  unreadCount = 0;
  notificationsLoading = false;
  loadingMore = false;
  hasMore = true;
  notificationDetailVisible = false;
  selectedNotification: NotificationDto | null = null;

  private pageIndex = 0;

  @Output() readonly unreadCountChange = new EventEmitter<number>();

  constructor(
    private readonly notificationService: NotificationDataService,
    private readonly languageService: LanguageService
  ) {}

  @HostListener('document:keydown.escape')
  onEscKey() {
    if (this.sidebarVisible) this.close();
  }

  open() {
    this.sidebarVisible = true;
    this.resetAndLoad();
  }

  close() {
    this.sidebarVisible = false;
    this.onSidebarHide();
  }

  loadUnreadCount() {
    this.notificationService.GetUnreadNotificationsCount().subscribe({
      next: (res) => {
        const raw = res?.resource;
        if (typeof raw === 'number') {
          this.unreadCount = Math.max(0, raw);
        } else {
          const nested = res?.data?.unreadNotificationsCount ?? res?.resource?.unreadNotificationsCount;
          this.unreadCount = Math.max(0, Number(nested) || 0);
        }
        this.unreadCountChange.emit(this.unreadCount);
      },
      error: () => {
        this.unreadCount = 0;
        this.unreadCountChange.emit(0);
      },
    });
  }

  onSidebarHide() {
    this.loadUnreadCount();
  }

  onBodyScroll(event: Event) {
    if (this.loadingMore || !this.hasMore) return;
    const el = event.target as HTMLElement;
    const threshold = 80;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold) {
      this.loadMore();
    }
  }

  openNotificationDetails(notification: NotificationDto) {
    this.selectedNotification = notification;
    this.notificationDetailVisible = true;
    if (!this.isNotificationRead(notification)) {
      this.markAsRead(notification.id);
    }
  }

  isNotificationRead(notification: NotificationDto): boolean {
    const r = notification.isRead as string | boolean | undefined;
    if (typeof r === 'boolean') return r;
    return r === 'true' || r === '1' || r === 'yes';
  }

  markAsRead(id: string) {
    this.notificationService.ReadNotification(id).subscribe({
      next: () => {
        this.setNotificationRead(id);
        if (this.unreadCount > 0) {
          this.unreadCount--;
          this.unreadCountChange.emit(this.unreadCount);
        }
      },
    });
  }

  markAllAsRead() {
    this.notificationService.ReadAll().subscribe({
      next: () => {
        this.notifications = [];
        this.unreadCount = 0;
        this.unreadCountChange.emit(0);
      },
    });
  }

  formatNotificationDate(value: string | undefined): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const isAr = this.languageService.currentLanguage === 'ar';
    if (diffMins < 1) return isAr ? 'الآن' : 'Just now';
    if (diffMins < 60) return isAr ? `منذ ${diffMins} د` : `${diffMins}m ago`;
    if (diffHours < 24) return isAr ? `منذ ${diffHours} س` : `${diffHours}h ago`;
    if (diffDays < 7) return isAr ? `منذ ${diffDays} يوم` : `${diffDays}d ago`;
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  trackByNotificationId(_index: number, item: NotificationDto): string {
    return item.id;
  }

  private resetAndLoad() {
    this.pageIndex = 0;
    this.hasMore = true;
    this.notifications = [];
    this.fetchPage(true);
  }

  private loadMore() {
    this.pageIndex++;
    this.fetchPage(false);
  }

  private fetchPage(isFirstPage: boolean) {
    if (isFirstPage) {
      this.notificationsLoading = true;
    } else {
      this.loadingMore = true;
    }

    this.notificationService.GetNotificationsList(this.pageIndex, PAGE_SIZE).subscribe({
      next: (res) => {
        const list = res?.data || res?.resource || [];
        const items: NotificationDto[] = Array.isArray(list)
          ? list.map((n: any) => this.mapNotification(n))
          : [];

        if (isFirstPage) {
          this.notifications = items;
        } else {
          this.notifications = [...this.notifications, ...items];
        }

        this.hasMore = items.length >= PAGE_SIZE;
        this.notificationsLoading = false;
        this.loadingMore = false;
      },
      error: () => {
        this.notificationsLoading = false;
        this.loadingMore = false;
        if (isFirstPage) {
          this.notifications = [];
        }
      },
    });
  }

  private setNotificationRead(id: string) {
    const n = this.notifications.find((x) => x.id === id);
    if (n) n.isRead = 'true';
  }

  private mapNotification(api: any): NotificationDto {
    return {
      id: api.notificationId || api.id || '',
      title: api.title || '',
      message: api.body || api.message || '',
      description: api.description || '',
      isRead: api.isRead || '',
      createdAt: api.createdAt || '',
    };
  }
}
